import React, { useCallback, useEffect, useRef, useState } from "react";
import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { css, Global } from "@emotion/react";
import { useEasySizeInstance } from "./use-easy-size-instance";
import { EasySizeStyles } from "./easy-size-styles";
import { SwarovskiButton } from "../../../components/_shared-components/ui-elements/SwarovskiButton";
import { FONTS } from "../../../styles/fonts";
import { useOpaqueValue } from "../../../hooks/use-opaque-value";
import { ESProductConfig } from "./get-easysize-product";
import { EasySizeConfiguration, EasySizeInstance } from "./types";
import type { TranslatedValue } from "@swo/storefront";
import { T } from "../../../components/T";

export const EasySizeComponent: ReactTFC<{
  esConfiguration: ESProductConfig;
  currentlySelectedSize: { data: string; label: string } | undefined;
  onSizeSelected: (sizeName: string) => void;
  easySizeTranslations: EasySizeTranslations;
}> = props => {
  const [recommendedSizeName, setRecommendedSizeName] = useState<string | null>(null);
  const onSizeSelectedRef = useOpaqueValue(props.onSizeSelected);
  const hasInteracted = useRef(false);

  const configuration: Omit<EasySizeConfiguration, "loaded"> = {
    ...props.esConfiguration.widget,
    size_update_function: size => {
      if (typeof size === "string") {
        setRecommendedSizeName(size);

        if (hasInteracted.current) {
          onSizeSelectedRef.current(size);
        }
      }
    },
  };

  const easySizeInstance = useEasySizeInstance(
    configuration,
    {
      src: props.esConfiguration.config.src,
      integrity: props.esConfiguration.config.integrity,
    },
    props.esConfiguration.key,
  );

  useEffect(() => {
    //@ts-expect-error
    window.ES = easySizeInstance;
    //@ts-expect-error
    window.config = configuration;
  }, [easySizeInstance]);

  const openEasySizeModalCallback = useCallback(
    (instance: EasySizeInstance) => () => {
      instance.open();

      hasInteracted.current = true;
    },
    [],
  );

  return (
    <div
      css={css`
        min-height: 20px;
        margin-bottom: 50px;
      `}
    >
      <Global styles={EasySizeStyles} />

      {easySizeInstance ? (
        <LoadedEasySize
          openEasySizeModalCallback={openEasySizeModalCallback(easySizeInstance.instance)}
          currentlySelectedSize={props.currentlySelectedSize}
          recommendedSize={recommendedSizeName}
          easySizeTranslations={props.easySizeTranslations}
        />
      ) : null}
    </div>
  );
};

export type EasySizeTranslations = {
  yourRecommendedSizeIs: TranslatedValue;
  openModalAgain: TranslatedValue;
  checkYourSizeCta: TranslatedValue;
};

const LoadedEasySize: ReactTFC<{
  openEasySizeModalCallback: () => void;
  currentlySelectedSize: { data: string; label: string } | undefined;
  recommendedSize: string | null;
  easySizeTranslations: EasySizeTranslations;
}> = ({ currentlySelectedSize, easySizeTranslations: t, openEasySizeModalCallback, recommendedSize }) => {
  return (
    <div>
      {recommendedSize ? (
        <span css={FONTS.SwarovskiFutura300}>
          <T>{t.yourRecommendedSizeIs}</T> {recommendedSize}.{" "}
          <button onClick={openEasySizeModalCallback} className={"u-underline"} css={FONTS.SwarovskiFutura300}>
            <T>{t.openModalAgain}</T>
          </button>
        </span>
      ) : (
        <SwarovskiButton as={"button"} onClick={openEasySizeModalCallback} variant={"green"} size={"small"}>
          <T>{t.checkYourSizeCta}</T>
        </SwarovskiButton>
      )}
    </div>
  );
};
